import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useRyftManagementCheckoutPaymentInitiate } from "~/hooks/api/v2/ryftManagement/useRyftManagementCheckoutPaymentInitiate";
import { useRyftManagementPaymentUpdate } from "~/hooks/api/v2/ryftManagement/useRyftManagementPaymentUpdate";
import { useRyftManagementOrderPaymentInitiate } from "~/hooks/api/v2/ryftManagement/useRyftManagementOrderPaymentInitiate";
import { RyftCardPaymentContext } from "./RyftCardPaymentProvider.shared";
import type { SafeReturnType } from "./RyftCardPaymentProvider.shared";
import type {
  RyftPaymentInitiateResponseType,
  RyftOrderPaymentInitiateResponseType,
} from "@sellar-io/api-v2/types";
import {
  RyftCardFormInstance,
  RyftPaymentAttemptResponse,
  RyftValidationChangeEvent,
} from "@ryftpay/react";

const ryftPublicKey = import.meta.env.VITE_RYFT_PUBLIC_KEY ?? "";

export const RyftCardPaymentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const cardFormInstanceRef = useRef<RyftCardFormInstance>(null);
  const [isAttemptingPayment, setIsAttemptingPayment] = useState(false);
  const [cardFormValidationEvent, setCardFormValidationEvent] =
    useState<RyftValidationChangeEvent | null>(null);
  const [paymentSession, setPaymentSession] =
    useState<RyftPaymentInitiateResponseType | null>(null);
  const paymentSessionId = paymentSession?.id ?? null;
  const clientSecret = paymentSession?.clientSecret ?? null;
  const ryftAccountId = paymentSession?.ryftAccountId ?? null;
  const paymentSessionStatus = paymentSession?.status ?? null;

  const {
    mutateAsync: initiateCheckoutPayment,
    isPending: isInitiatingCheckoutPaymentSession,
  } = useRyftManagementCheckoutPaymentInitiate();

  const {
    mutateAsync: initiateOrderPayment,
    isPending: isInitiatingOrderPaymentSession,
  } = useRyftManagementOrderPaymentInitiate();

  const isInitiatingPaymentSession =
    isInitiatingCheckoutPaymentSession || isInitiatingOrderPaymentSession;

  const { mutateAsync: updatePayment, isPending: isUpdatingPaymentSession } =
    useRyftManagementPaymentUpdate();

  const initiateCheckoutPaymentSession = useCallback(
    async ({
      amount,
      supplierId,
    }: {
      amount: number;
      supplierId: number;
    }): Promise<SafeReturnType<{ clientSecret: string }>> => {
      setPaymentSession(null);
      setCardFormValidationEvent(null);

      try {
        const response = await initiateCheckoutPayment({
          data: { amount, supplierId },
        });

        setPaymentSession(response);

        return {
          data: { clientSecret: response.clientSecret },
          error: null,
        };
      } catch (error) {
        return {
          data: null,
          error:
            error instanceof Error
              ? error
              : new Error("Unable to initiate payment."),
        };
      }
    },
    [initiateCheckoutPayment],
  );

  const initiateOrderPaymentSession = useCallback(
    async ({
      orderId,
    }: {
      orderId: number;
    }): Promise<SafeReturnType<RyftOrderPaymentInitiateResponseType>> => {
      setPaymentSession(null);
      setCardFormValidationEvent(null);

      try {
        const response = await initiateOrderPayment({ orderId });
        setPaymentSession(response);
        return { data: response, error: null };
      } catch (error) {
        return {
          data: null,
          error:
            error instanceof Error
              ? error
              : new Error("Unable to prepare your card payment."),
        };
      }
    },
    [initiateOrderPayment],
  );

  const updatePaymentSession = useCallback(
    async ({
      orderId,
      paymentSessionId,
    }: {
      orderId: number;
      paymentSessionId: string;
    }): Promise<SafeReturnType<{ id: string }>> => {
      try {
        const response = await updatePayment({
          paymentSessionId,
          data: {
            orderId,
          },
        });

        return {
          data: { id: response.id },
          error: null,
        };
      } catch (error) {
        return {
          data: null,
          error:
            error instanceof Error
              ? error
              : new Error("Unable to initiate payment."),
        };
      }
    },
    [updatePayment],
  );

  const payWithRyft = useCallback(async (): Promise<
    SafeReturnType<RyftPaymentAttemptResponse>
  > => {
    setIsAttemptingPayment(true);
    try {
      const ryftCardInstance = cardFormInstanceRef.current;

      if (!ryftCardInstance) {
        return {
          data: null,
          error: new Error("Ryft card instance not found"),
        };
      }

      if (!paymentSession) {
        return {
          data: null,
          error: new Error("Payment session not found"),
        };
      }

      // Await the payment attempt
      const result = await ryftCardInstance.attemptPayment({});

      if (!result) {
        return {
          data: null,
          error: new Error("Unable to initiate payment result."),
        };
      }

      if (result.type === "action-required") {
        return {
          data: null,
          error: new Error("Additional action required"),
        };
      }

      setPaymentSession({
        ...paymentSession,
        status: result.paymentSession.status ?? paymentSession.status,
      });

      if (
        result.paymentSession.status === "Approved" ||
        result.paymentSession.status === "Captured" ||
        result.paymentSession.status === "Processing"
      ) {
        return {
          data: result,
          error: null,
        };
      }

      if (result.paymentSession.lastError) {
        console.error(result.userFacingErrorMessage);
        return {
          data: null,
          error: new Error(result.userFacingErrorMessage),
        };
      }

      return {
        data: null,
        error: new Error("Payment failed"),
      };
    } catch (error) {
      console.error(error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("Unable to initiate payments."),
      };
    } finally {
      setIsAttemptingPayment(false);
    }
  }, [cardFormInstanceRef, paymentSession]);

  const ryftPayment = useMemo(
    () => ({
      isInitiatingPaymentSession,
      isUpdatingPaymentSession,
      isAttemptingPayment,
      paymentSessionId,
      clientSecret,
      ryftPublicKey,
      cardFormInstanceRef,
      cardFormValidationEvent,
      ryftAccountId,
      paymentSessionStatus,
      setCardFormValidationEvent,
      initiateCheckoutPaymentSession,
      initiateOrderPaymentSession,
      payWithRyft,
      updatePaymentSession,
    }),
    [
      isInitiatingPaymentSession,
      isUpdatingPaymentSession,
      isAttemptingPayment,
      paymentSessionId,
      clientSecret,
      cardFormInstanceRef,
      cardFormValidationEvent,
      ryftAccountId,
      paymentSessionStatus,
      setCardFormValidationEvent,
      initiateCheckoutPaymentSession,
      initiateOrderPaymentSession,
      payWithRyft,
      updatePaymentSession,
    ],
  );

  return (
    <RyftCardPaymentContext.Provider value={ryftPayment}>
      {children}
    </RyftCardPaymentContext.Provider>
  );
};
